import { create } from 'zustand'
import type {
  Startup,
  StartupMembership,
  StartupWithMembership,
} from '@/types/api'

/**
 * App-level state for the post-auth shell:
 *   - which startup is currently active (the one every API call scopes to)
 *   - the caller's membership/role on that startup
 *   - the full list of startups the user belongs to (for the org switcher)
 *   - the open gap count, used by the sidebar nav badge
 *
 * Theme state is intentionally NOT here. The active theme is owned by
 * `useTheme` + the `data-theme` attribute on `<html>`. Putting theme state
 * in a Zustand slice would force a re-render of every subscriber on every
 * theme flip, and fighting `data-theme` for primacy never ends well.
 *
 * Persistence is also intentionally absent. The active startup is
 * re-derived on every app boot via `useStartupBootstrap` (which calls
 * `startupService.list()`), so persisting the slice would just create a
 * stale-cache window on first paint after the user adds/removes a startup
 * from another tab. No persist middleware.
 */
interface AppState {
  startup: Startup | null
  membership: StartupMembership | null
  startups: StartupWithMembership[]
  openGapCount: number

  /**
   * Set the active startup + membership atomically. Pass a single
   * `StartupWithMembership` so the two halves can never desync (which
   * would let the UI render with one user's role on another user's
   * startup, a security-relevant footgun).
   */
  setActiveStartup: (entry: StartupWithMembership | null) => void

  /** Replace the full list. Idempotent if the list contents are equal. */
  setStartups: (startups: StartupWithMembership[]) => void

  /** Sidebar badge counter — driven by the SSE updates stream. */
  setOpenGapCount: (count: number) => void

  /** Reset on sign-out. */
  clear: () => void
}

const initialState = {
  startup: null,
  membership: null,
  startups: [] as StartupWithMembership[],
  openGapCount: 0,
}

export const useAppStore = create<AppState>((set) => ({
  ...initialState,

  setActiveStartup: (entry) =>
    set({
      startup: entry?.startup ?? null,
      membership: entry?.membership ?? null,
    }),

  setStartups: (startups) => set({ startups }),

  setOpenGapCount: (count) => set({ openGapCount: Math.max(0, count) }),

  clear: () => set(initialState),
}))
